import { useMutation, useQueryClient } from 'react-query'
import { useAuth0 } from '../../../contexts/auth0-context'
import endpoints from '../../../API/endpoints'
import callAPI from '../../../API/callSecureAPI'
import { LinkingItem, LinkingItemsIds, ROADMAP_SHARED_LINK_TYPES } from '../../../components/Linking/Linking.types'
import useInvalidateLinkingQueries from './useInvalidateLinkingQueries'
import { useInvalidateActivitiesQuery } from '../useInvalidateActivitiesQuery'

type UseUpdateLinkedItemsMutationProps = {
    onSuccess?: () => void
    onError?: (error: unknown) => void
}

type UpdateLinkedItems = {
    changes: {
        linkedItems: LinkingItemsIds
        unlinkedItems: LinkingItemsIds
    }
}

export const useUpdateLinkedItemsMutation = ({ onSuccess, onError }: UseUpdateLinkedItemsMutationProps = {}) => {
    const { getTokenSilently, isAuthenticated } = useAuth0()

    const queryClient = useQueryClient()

    const { createInvalidationQueries } = useInvalidateLinkingQueries()
    const { invalidateActivitiesQuery } = useInvalidateActivitiesQuery()

    const getEndpointByType = (type: LinkingItem['type']) => {
        switch (type) {
            case 'activity':
                return endpoints.updateActivityLinkedItems
            case 'evidence':
                return endpoints.updateEvidenceLinkedItems
            case 'valueMessage':
                return endpoints.updateValueMessageLinkedItems
            case 'strategicImperative':
                return endpoints.updateStrategicImperativeLinkedItems
            case 'tppCell':
                return endpoints.updateTppCellLinkedItems
            default:
                return null
        }
    }

    const updateLinkedItems = async ({
        id,
        type,
        selectedIds,
        roadmapId,
    }: {
        id: LinkingItem['_id']
        type: LinkingItem['type']
        selectedIds: LinkingItemsIds
        // Required for a roadmap-shared type (see ROADMAP_SHARED_LINK_TYPES), so the backend
        // can scope unlinking to this roadmap's own contributions. Ignored for every other type.
        roadmapId?: string
    }) => {
        if (!isAuthenticated) {
            throw new Error('User must be authenticated to update linked items')
        }

        const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
        if (!token) {
            throw new Error('Failed to get authentication token')
        }

        const endpoint = getEndpointByType(type)
        if (!endpoint) {
            throw new Error(`Invalid linking type: ${type}`)
        }

        const isRoadmapShared = ROADMAP_SHARED_LINK_TYPES.includes(type)

        if (isRoadmapShared && !roadmapId) {
            throw new Error(`roadmapId is required to update a ${type}'s linked items`)
        }

        const url = isRoadmapShared ? `${endpoint.url}${id}/roadmap/${roadmapId}` : endpoint.url + id

        const response = await callAPI(url, endpoint.method, token, selectedIds)

        if (!response.success) {
            throw new Error(response.error || 'Failed to update linked items')
        }

        return response as UpdateLinkedItems
    }

    // @TODO linking - primaryEvidence, primaryValueMessages

    return useMutation(updateLinkedItems, {
        onSuccess: async (response, variables) => {
            const invalidationPromises = [queryClient.invalidateQueries(['linkedItems', variables.id, variables.type])]

            if (Object.keys(response.changes.linkedItems).length > 0) {
                invalidationPromises.push(...createInvalidationQueries(response.changes.linkedItems))
            }

            if (Object.keys(response.changes.unlinkedItems).length > 0) {
                invalidationPromises.push(...createInvalidationQueries(response.changes.unlinkedItems))
            }

            if (variables.type === 'activity') {
                invalidationPromises.push(invalidateActivitiesQuery())
            }

            await Promise.all(invalidationPromises)

            onSuccess?.()
        },
        onError: (error) => {
            console.error('Error updating linked items:', error)
            onError?.(error)
        },
    })
}
